/* eslint-disable max-lines */
import { BookingCancellationCategoryCode, BookingCancellationReasonCode } from '@di/servicing';

type BookingCancellationReason = {
    categoryCode: BookingCancellationCategoryCode;
    reasonCode: BookingCancellationReasonCode;
    title: string;
    externalDescription: string;
};

const bookingCancellationReasons: BookingCancellationReason[] = [
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.DelayProblemComplexJob,
        title: 'Technician Unable to Attend',
        externalDescription: 'Our technician\'s previous job took longer than expected due to its complexity, which meant they were unable to attend this appointment as scheduled. We will rebook this visit as a priority.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.DelayProblemTraffic,
        title: 'Technician Unable to Attend',
        externalDescription: 'Our technician was delayed by traffic on the way to this appointment and was unable to attend as scheduled. We will rebook this visit as a priority.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.OhsWeather,
        title: 'Unsafe Site Conditions',
        externalDescription: 'This appointment was cancelled because weather conditions on the day made it unsafe for our technician to carry out the job. We will reschedule once conditions improve.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.Other,
        title: 'Unforeseen Circumstances',
        externalDescription: 'This appointment could not proceed due to an unforeseen issue affecting our technician on the day. We will rebook this visit as soon as possible.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.AddressNotFoundInMap,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician was unable to locate the property at the address provided and could not gain the access required to complete this job.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.ApplianceOrEquipmentAccess,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician gained access to the property but could not get the required access to one or more appliances, so the job could not be completed.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.AccessProblemOther,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician attended the property but was unable to gain the access required to complete this job.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.RoadToPropertyNotAccessible,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician was unable to reach the property because the surrounding roads were not accessible on the day.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.UnableToFindParking,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician was unable to find suitable parking near the property and could not gain the access required to complete this job.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.TechHealthProblem,
        title: 'Technician Unable to Attend',
        externalDescription: 'Our technician was unable to attend this appointment due to a personal or health-related matter. The visit will be rebooked.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.TimeManagementProblem,
        title: 'Technician Unable to Attend',
        externalDescription: 'Our technician ran behind schedule on the day and was unable to attend this appointment as planned. The visit will be rebooked.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.FieldStaff,
        reasonCode: BookingCancellationReasonCode.VehicleEquipmentProblem,
        title: 'Technician Unable to Attend',
        externalDescription: 'Our technician was unable to attend this appointment due to a vehicle or equipment fault. The visit will be rebooked.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.KeyDidNotWork,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician was unable to gain the access required to complete this job, as the key provided by the agency did not open the property.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.KeyUnavailable,
        title: 'Unable to Gain Property Access',
        externalDescription: 'Our technician was unable to gain the access required to complete this job, as no key was available for collection at the time of the appointment.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.NoLongerManaged,
        title: 'Property No Longer on Service',
        externalDescription: 'This property is no longer managed by this agency and has been removed from the service.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.NoPowerAtProperty,
        title: 'No Power at Property',
        externalDescription: 'There was no power connected at the property, so the service could not be completed. The appointment will be rescheduled once power is restored.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.OccupantDetailsChanged,
        title: 'Occupant Details Changed',
        externalDescription: 'The agency advised that the tenant details for this property have changed, so the booking needs to be reconfirmed before the appointment can proceed.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.Other,
        title: 'Unforeseen Circumstances',
        externalDescription: 'This appointment could not proceed due to an unforeseen issue outside our control.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.AgencyRelated,
        reasonCode: BookingCancellationReasonCode.AgencyOrLandlordRequest,
        title: 'Request from Agency/Landlord',
        externalDescription: 'The agency or landlord contacted us directly and requested that this appointment be cancelled.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.UnrestrainedDogOnSite,
        title: 'Dog on Property',
        externalDescription: 'Our technician could not safely access the property due to an unrestrained dog on site. Please ensure pets are secured ahead of the next visit.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.NoOneHome,
        title: 'No One Home',
        externalDescription: 'Our technician attended at the scheduled time, but nobody was home to provide access to the property.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.RefusedAccess,
        title: 'Entry Not Available',
        externalDescription: 'Our technician attended at the scheduled time, but the occupant declined to allow entry to the property.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.RescheduleRequested,
        title: 'Reschedule Requested',
        externalDescription: 'The occupant contacted us to request a different appointment time, and this booking has been rescheduled accordingly.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.NoAdultPresent,
        title: 'No One Home',
        externalDescription: 'Our technician attended at the scheduled time, but no adult was present to authorise access to the property.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.Ohs,
        title: 'Occupational Health & Safety',
        externalDescription: 'The appointment was cancelled after our technician identified a health and safety concern on site that needs to be resolved before the job can proceed.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.OccupantRelated,
        reasonCode: BookingCancellationReasonCode.Other,
        title: 'Unforeseen Circumstances',
        externalDescription: 'This appointment could not proceed due to an unforeseen issue raised by the occupant.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.IncorrectQuote,
        title: 'Internal Scheduling Issue',
        externalDescription: 'This appointment was cancelled because the original quote did not accurately reflect the scope of work required. It will be rebooked once this has been corrected.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.ItIssues,
        title: 'Internal Scheduling Issue',
        externalDescription: 'This appointment was cancelled due to a technical system issue on our side. It will be rebooked as soon as possible.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.RescheduleForLegislationUpgrade,
        title: 'Reschedule for Legislation Upgrade',
        externalDescription: 'This appointment has been rescheduled to align with a legislated compliance upgrade required at this property.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.RosterIssue,
        title: 'Internal Scheduling Issue',
        externalDescription: 'This appointment was cancelled due to a technician rostering issue on our side. It will be rebooked as soon as possible.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.SchedulingIssue,
        title: 'Internal Scheduling Issue',
        externalDescription: 'This appointment was cancelled due to a scheduling conflict on our side. It will be rebooked as soon as possible.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.ServiceSheetRejected,
        title: 'Internal Scheduling Issue',
        externalDescription: 'We will need to re-attend because we found more complex issues that required further investigation. We will re-book a follow up apppointment.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.UrgentJobsAdded,
        title: 'Internal Scheduling Issue',
        externalDescription: 'This appointment was cancelled to prioritise an urgent job that required immediate attention. It will be rebooked as soon as possible.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.InternalStaff,
        reasonCode: BookingCancellationReasonCode.WrongBooking,
        title: 'Internal Scheduling Issue',
        externalDescription: 'This appointment was cancelled because it was booked in error. A corrected appointment will be scheduled.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.Other,
        reasonCode: BookingCancellationReasonCode.Covid19Lockdown,
        title: 'COVID-19 Lockdown',
        externalDescription: 'The appointment was cancelled due to a COVID-19 lockdown restricting access to the property.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.Other,
        reasonCode: BookingCancellationReasonCode.QueensMemorialPublicHoliday,
        title: 'Public Holiday',
        externalDescription: 'The appointment was cancelled due to the public holiday declared for this day.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.Other,
        reasonCode: BookingCancellationReasonCode.Unknown,
        title: 'Unforeseen Circumstances',
        externalDescription: 'This appointment could not proceed and the specific reason was not recorded at the time.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.Other,
        reasonCode: BookingCancellationReasonCode.UnknownNoTenantComms,
        title: 'Unforeseen Circumstances',
        externalDescription: 'This appointment could not proceed as we were unable to reach the tenant to confirm the appointment details.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.TenantPortal,
        reasonCode: BookingCancellationReasonCode.NotLivingAtProperty,
        title: 'Tenant No Longer at Property',
        externalDescription: 'The occupant advised via the Tenant Portal that they are no longer living at this residence.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.TenantPortal,
        reasonCode: BookingCancellationReasonCode.NoSuitableSlotAvailable,
        title: 'Reschedule Requested',
        externalDescription: 'The occupant advised via the Tenant Portal that they require the booking to be rescheduled.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.TenantPortal,
        reasonCode: BookingCancellationReasonCode.Other,
        title: 'Unforeseen Circumstances',
        externalDescription: 'This appointment was cancelled by the occupant via the Tenant Portal.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.TenantPortal,
        reasonCode: BookingCancellationReasonCode.RescheduleRequested,
        title: 'Reschedule Requested',
        externalDescription: 'The occupant advised via the Tenant Portal that they require the booking to be rescheduled.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.SmartSmokeAlarm,
        reasonCode: BookingCancellationReasonCode.MissingStock,
        title: 'Missing Stock',
        externalDescription: 'Our technician did not have the correct smoke alarm stock required to complete this installation. The appointment will be rescheduled once stock is available.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.SmartSmokeAlarm,
        reasonCode: BookingCancellationReasonCode.NoPowerAtProperty,
        title: 'No Power at Property',
        externalDescription: 'There was no power at the property, so the Smart smoke alarm hub could not be installed. The appointment will be rescheduled once power is restored.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.SmartSmokeAlarm,
        reasonCode: BookingCancellationReasonCode.AsbestosPresent,
        title: 'Asbestos Present',
        externalDescription: 'Suspected or confirmed asbestos-containing material (ACM) was identified at the property, so a licensed contractor is required to attend before the Smart smoke alarm can be installed.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.SmartSmokeAlarm,
        reasonCode: BookingCancellationReasonCode.NoSuitableInstallationSpotForHub,
        title: 'Unsuitable Installation Location',
        externalDescription: 'Our technician assessed the property and could not identify a suitable location to install the Smart smoke alarm hub.',
    },
    {
        categoryCode: BookingCancellationCategoryCode.SmartSmokeAlarm,
        reasonCode: BookingCancellationReasonCode.SmartSmokeAlarmNotEligibleFip,
        title: 'Smart Smoke Alarm Not Eligible (FIP)',
        externalDescription: 'This property is connected to a Fire Indicator Panel (FIP), so it is not currently eligible for a Smart Smoke Alarm installation.',
    },
];

const bookingCancellationReasonByKey: Record<string, BookingCancellationReason> = {};
for (const reason of bookingCancellationReasons) {
    bookingCancellationReasonByKey[`${reason.categoryCode}:${reason.reasonCode}`] = reason;
}

const unknownBookingCancellationReason = bookingCancellationReasonByKey[
    `${BookingCancellationCategoryCode.Other}:${BookingCancellationReasonCode.Unknown}`
];

class BookingCancellationReasonUtil {
    static getByCode(
        categoryCode: string | null,
        reasonCode: string | null
    ): BookingCancellationReason {
        if (
            categoryCode === null
            || categoryCode === ''
            || reasonCode === null
            || reasonCode === ''
        ) {
            return unknownBookingCancellationReason;
        }

        return bookingCancellationReasonByKey[`${categoryCode}:${reasonCode}`]
          ?? unknownBookingCancellationReason;
    }

    static getTitle(
        categoryCode: string | null,
        reasonCode: string | null
    ): string {
        return BookingCancellationReasonUtil.getByCode(categoryCode, reasonCode).title;
    }

    static getExternalDescription(
        categoryCode: string | null,
        reasonCode: string | null
    ): string {
        return BookingCancellationReasonUtil
            .getByCode(categoryCode, reasonCode).externalDescription;
    }
}

export {
    BookingCancellationReasonUtil,
    bookingCancellationReasons,
    type BookingCancellationReason,
};
